import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useOrg } from '../context/OrgContext';
import { requestOtp, verifyOtp, signUp } from '../api/auth';
import { setItem } from '../utils/storage';

type Step = 'phone' | 'otp' | 'signup';

const LoginModal: React.FC = () => {
  const { isLoginOpen, closeLogin } = useAuth();
  const org = useOrg();
  const [step, setStep] = useState<Step>('phone');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupData, setSignupData] = useState({ first_name: '', last_name: '', email: '', gender: '' });

  if (!isLoginOpen) return null;

  const reset = () => {
    setStep('phone'); setMobile(''); setOtp(''); setError(''); setSignupData({ first_name: '', last_name: '', email: '', gender: '' });
  };

  const handleClose = () => { reset(); closeLogin(); };

  const handlePhoneSubmit = async () => {
    if (!/^\d{10}$/.test(mobile)) { setError('Enter a valid 10-digit mobile number'); return; }
    setLoading(true); setError('');
    const result = await requestOtp(mobile);
    setLoading(false);
    if (result.userExists) {
      setStep('otp');
    } else if (!result.userExists) {
      setStep('signup');
    } else {
      setError(result.message || 'Failed to send OTP');
    }
  };

  const handleOtpSubmit = async () => {
    if (otp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    setLoading(true); setError('');
    const result = await verifyOtp(mobile, otp);
    setLoading(false);
    if (result.success && result.token) {
      setItem('authToken', result.token);
      setItem('userId', result.userId || '');
      setItem('role', result.role || 'customer');
      if (result.refreshToken) setItem('refreshToken', result.refreshToken);
      if (result.orgPreferences) setItem('org_preferences', JSON.stringify(result.orgPreferences));
      handleClose();
      window.location.reload();
    } else {
      setError('Invalid OTP. Please try again.');
    }
  };

  const handleSignup = async () => {
    if (!signupData.first_name.trim()) { setError('Enter first name'); return; }
    if (!signupData.last_name.trim()) { setError('Enter last name'); return; }
    if (!signupData.gender) { setError('Select gender'); return; }
    if (!/\S+@\S+\.\S+/.test(signupData.email)) { setError('Enter valid email'); return; }
    setLoading(true); setError('');
    const result = await signUp({ ...signupData, mobile, country_code: '91' });
    setLoading(false);
    if (result.success) {
      setStep('otp');
    } else {
      setError(result.message || 'Signup failed');
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-box" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}>✕</button>

        <div className="modal-header">
          <div className="modal-logo">
            <img src={org?.ecommerce_logo || org?.image || '/logo.png'} alt={org?.org_name || 'Store'} className="modal-logo__img" />
          </div>
          <h2>Welcome to {org?.org_name || 'Our Store'}</h2>
          <p>{step === 'signup' ? 'Create your account' : step === 'otp' ? `OTP sent to +91 ${mobile}` : 'Sign in to continue'}</p>
        </div>

        {error && <div className="modal-error">{error}</div>}

        {step === 'phone' && (
          <>
            <div className="input-group">
              <span className="input-prefix">+91</span>
              <input
                type="tel" maxLength={10} placeholder="Mobile number"
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && handlePhoneSubmit()}
                className="modal-input with-prefix"
              />
            </div>
            <button className="modal-btn" onClick={handlePhoneSubmit} disabled={loading}>
              {loading ? 'Sending...' : 'Continue'}
            </button>
          </>
        )}

        {step === 'otp' && (
          <>
            <input
              type="tel" maxLength={6} placeholder="Enter 6-digit OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleOtpSubmit()}
              className="modal-input"
            />
            <button className="modal-btn" onClick={handleOtpSubmit} disabled={loading || otp.length < 6}>
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>
            <button className="modal-link" onClick={() => { setStep('phone'); setOtp(''); }}>← Change number</button>
          </>
        )}

        {step === 'signup' && (
          <>
            <div className="modal-grid-2">
              <input placeholder="First name" className="modal-input" value={signupData.first_name}
                onChange={(e) => setSignupData({ ...signupData, first_name: e.target.value })} />
              <input placeholder="Last name" className="modal-input" value={signupData.last_name}
                onChange={(e) => setSignupData({ ...signupData, last_name: e.target.value })} />
            </div>
            <select className="modal-input modal-select" value={signupData.gender}
              onChange={(e) => setSignupData({ ...signupData, gender: e.target.value })}>
              <option value="">Select gender</option>
              <option value="M">Male</option>
              <option value="F">Female</option>
              <option value="O">Other</option>
            </select>
            <input type="email" placeholder="Email address" className="modal-input" value={signupData.email}
              onChange={(e) => setSignupData({ ...signupData, email: e.target.value })} />
            <div className="input-group">
              <span className="input-prefix">+91</span>
              <input type="tel" maxLength={10} placeholder="Mobile number" className="modal-input with-prefix"
                value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))} />
            </div>
            <button className="modal-btn" onClick={handleSignup} disabled={loading}>
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
            <button className="modal-link" onClick={() => setStep('phone')}>Already have an account? Sign in</button>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
